import { PageTitle as SharedPageTitle } from '../components/layout/PageHeader';
/**
 * InvoiceResultsPage - Invoice processing results display and editing
 * Shows the results of AI-powered invoice scanning with comprehensive editing capabilities
 * Features: Edit/Preview mode toggle, inline editing, real-time validation, confidence indicator
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';

import { yupResolver } from '@hookform/resolvers/yup';
import { toast } from 'react-hot-toast';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { Icon } from '../components/ui/Icon';
import { FormField } from '../components/forms/FormField';
import { ServiceTypeSelect } from '../components/forms/ServiceTypeSelect';
import { SelectField } from '../components/forms/SelectField';
import { BAG_SIZE_OPTIONS } from '../constants';

import {
  queryKeys,
  useTransactionDetails,
  useTransactionType,
  useTransactionOperations,
} from '../hooks/useApi';
import { useCurrency } from '../hooks/useCurrency';

import type { Invoice, ConsumptionUnit, ServiceType } from '../types/api';
import { InvoiceEditForm } from '../types/forms';
import {
  invoiceSchemas,
  SUPPORTED_CURRENCY_CODES as SUPPORTED_INVOICE_CURRENCIES,
} from '../components/forms/validation';
import {
  formatNumberEs,
  formatDate,
  formatDateShort,
  formatServiceType,
  getUnit,
  formatConfidence,
  formatIsoDateForInput,
  calculateBagKilograms,
} from '../utils/formatters';
import {
  canEditTransaction,
  canDeleteTransaction,
  getTransactionTypeLabel,
  isSalesTransaction,
  FrontendTransactionType,
} from '../utils/transactions';
import styled from 'styled-components';

// ============================================================================
// Types
// ============================================================================

type InvoiceResultsPageProps = Record<string, never>;

type ViewMode = 'preview' | 'edit';

interface ConfidenceIndicatorProps {
  confidence: number;
  showDetails?: boolean;
}

// ============================================================================
// Styled Components
// ============================================================================

const ResultsContainer = styled.div`
  width: 100%;
  min-width: 0;
  max-width: none;
  margin: 0;
  padding: 0;
`;

const Header = styled.div`
  text-align: left;
  margin-bottom: 1.5rem;
`;

const Title = styled(SharedPageTitle)``;

const Subtitle = styled.p`
  margin: 0.5rem 0 0;
  max-width: 65ch;
  font-size: 0.875rem;
  line-height: 1.6;
  color: var(--text-muted);
`;

const ResultsGrid = styled.div`
  display: grid;
  gap: var(--space-xl);
  margin-bottom: var(--space-2xl);
`;

const InvoiceCard = styled(Card)`
  padding: var(--space-xl);
`;

const InvoiceHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--space-lg);
  padding-bottom: var(--space-md);
  border-bottom: 1px solid var(--border);
`;

const InvoiceTitle = styled.h2`
  margin: 0;
  color: var(--text-primary);
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-semibold);
`;

const StatusBadge = styled.span<{ confidence: number }>`
  padding: var(--space-xs) var(--space-sm);
  border-radius: var(--radius-sm);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  background: ${(props) =>
    props.confidence >= 90
      ? 'var(--success-600)'
      : props.confidence >= 70
        ? 'var(--warning-600)'
        : 'var(--danger-600)'};
  color: white;
`;

const EditFormContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-md);
  margin-bottom: var(--space-xl);

  @media (min-width: 480px) {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-lg);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 360px) {
    gap: var(--space-sm);
  }
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-md);
`;

const SectionTitle = styled.h3`
  margin: 0 0 var(--space-sm) 0;
  color: var(--text-primary);
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-semibold);
  border-bottom: 1px solid var(--border);
  padding-bottom: var(--space-xs);
`;

const UnitWarning = styled.div`
  display: flex;
  align-items: flex-start;
  gap: var(--space-sm);
  margin-bottom: var(--space-md);
  padding: var(--space-sm) var(--space-md);
  border: 1px solid var(--warning-500);
  border-radius: 8px;
  background: var(--warning-100);
  color: var(--text-primary);
  font-size: var(--font-size-sm);
`;

const ConfidenceContainer = styled.div`
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  margin-bottom: var(--space-md);
`;

const ConfidenceBar = styled.div<{ confidence: number }>`
  flex: 1;
  height: 8px;
  background: var(--neutral-200);
  border-radius: 4px;
  overflow: hidden;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: ${(props) => props.confidence}%;
    background: ${(props) =>
      props.confidence >= 90
        ? 'var(--success-600)'
        : props.confidence >= 70
          ? 'var(--warning-600)'
          : 'var(--danger-600)'};
    transition: width 0.3s ease;
  }
`;

const ConfidenceText = styled.span<{ confidence: number }>`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: ${(props) =>
    props.confidence >= 90
      ? 'var(--success-600)'
      : props.confidence >= 70
        ? 'var(--warning-600)'
        : 'var(--danger-600)'};
`;

const SaveCancelActions = styled.div`
  display: flex;
  gap: var(--space-md);
  justify-content: flex-end;
  margin-top: var(--space-xl);
  padding-top: var(--space-lg);
  border-top: 1px solid var(--border);
`;

const InvoiceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--space-md);
  margin-bottom: var(--space-xl);

  @media (min-width: 400px) {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-lg);
  }

  @media (min-width: 1024px) {
    grid-template-columns: repeat(3, 1fr);
  }

  @media (max-width: 360px) {
    gap: var(--space-sm);
  }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-xs);
`;

const FieldLabel = styled.label`
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-muted);
  text-transform: none;
  letter-spacing: 0.5px;
`;

const FieldValue = styled.div`
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
  word-break: break-word;
`;

const ActionsSection = styled.div`
  display: flex;
  gap: var(--space-md);
  justify-content: center;
  margin-top: var(--space-xl);
  padding-top: var(--space-xl);
  border-top: 1px solid var(--border);
`;

const BackButton = styled(Button)`
  min-width: 120px;
`;

const EditButton = styled(Button)`
  min-width: 120px;
`;

const ConfirmButton = styled(Button)`
  min-width: 120px;
`;

const ErrorContainer = styled.div`
  text-align: center;
  padding: var(--space-3xl);
`;

const ErrorIcon = styled.div`
  font-size: var(--font-size-3xl);
  margin-bottom: var(--space-lg);
  opacity: 0.6;
`;

const ErrorMessage = styled.p`
  margin: 0 0 var(--space-xl) 0;
  color: var(--text-muted);
  font-size: var(--font-size-lg);
  line-height: var(--line-height-relaxed);
`;

// ============================================================================
// Confidence Indicator Component
// ============================================================================

const ConfidenceIndicator: React.FC<ConfidenceIndicatorProps> = ({
  confidence,
  showDetails = true,
}) => {
  // Convert decimal confidence (0.98) to percentage (98)
  const confidencePercent = formatConfidence(confidence);

  const getConfidenceLevel = (conf: number) => {
    if (conf >= 90) return { level: 'Alta', color: 'var(--success-600)' };
    if (conf >= 70) return { level: 'Media', color: 'var(--warning-600)' };
    return { level: 'Baja', color: 'var(--danger-600)' };
  };

  const { level, color } = getConfidenceLevel(confidencePercent);

  return (
    <ConfidenceContainer>
      <ConfidenceBar confidence={confidencePercent} />
      <ConfidenceText confidence={confidencePercent}>
        {Math.round(confidencePercent)}%
      </ConfidenceText>
      {showDetails && (
        <span style={{ fontSize: 'var(--font-size-sm)', color }}>
          ({level} confianza)
        </span>
      )}
    </ConfidenceContainer>
  );
};

// ============================================================================
// Safe Data Transformation Utilities
// ============================================================================

const SERVICE_UNITS: Record<ServiceType, readonly ConsumptionUnit[]> = {
  electricidad: ['kWh'],
  agua: ['m³'],
  gas: ['m³', 'kWh'],
  'residuos-reciclables': ['kg', 'bolsas'],
  'residuos-organicos': ['kg', 'bolsas'],
  'residuos-generales': ['kg', 'bolsas'],
};

const DEFAULT_SERVICE_UNIT: Record<ServiceType, ConsumptionUnit> = {
  electricidad: 'kWh',
  agua: 'm³',
  gas: 'm³',
  'residuos-reciclables': 'kg',
  'residuos-organicos': 'kg',
  'residuos-generales': 'kg',
};

const isServiceType = (value: unknown): value is ServiceType =>
  typeof value === 'string' &&
  Object.prototype.hasOwnProperty.call(SERVICE_UNITS, value);

const isConsumptionUnit = (value: unknown): value is ConsumptionUnit =>
  value === 'kWh' || value === 'm³' || value === 'kg' || value === 'bolsas';

const UNIT_LABELS: Record<ConsumptionUnit, string> = {
  kWh: 'kWh',
  'm³': 'm³',
  kg: 'Kilogramos (kg)',
  bolsas: 'Bolsas',
};

const BAG_SIZE_SELECT_OPTIONS = BAG_SIZE_OPTIONS.map((option) => ({
  value: option.value,
  label: `${option.label} (${option.description})`,
}));

const normalizeStoredUnit = (unit: string | null | undefined): string =>
  unit?.trim() === 'm3' ? 'm³' : (unit?.trim() ?? '');

const isUnitAllowedForService = (
  serviceType: ServiceType,
  unit: string | null | undefined,
): boolean => {
  const normalized = normalizeStoredUnit(unit);
  return (
    isConsumptionUnit(normalized) &&
    SERVICE_UNITS[serviceType].includes(normalized)
  );
};

/**
 * Safely get current date in YYYY-MM-DD format
 */
const getCurrentDateString = (): string => {
  try {
    const isoString = new Date().toISOString();
    const datePart = isoString.split('T')[0];
    return datePart || '2024-01-01';
  } catch (error) {
    // Fallback to a safe default date
    return '2024-01-01';
  }
};

/**
 * Safely transforms invoice data to form data with proper validation
 * Prevents runtime errors during form initialization
 */
const safeTransformInvoiceToForm = (
  invoice: Invoice,
  fallbackCurrency: string,
): InvoiceEditForm => {
  try {
    // Validate required fields first
    const serviceType = invoice.service_type;
    if (!isServiceType(serviceType)) {
      throw new Error(`Invalid service type: ${invoice.service_type}`);
    }

    if (typeof invoice.consumption !== 'number' || isNaN(invoice.consumption)) {
      throw new Error(`Invalid consumption: ${invoice.consumption}`);
    }

    // Validate consumption unit matches service type
    const storedUnit = normalizeStoredUnit(invoice.consumption_unit);
    let consumptionUnit: ConsumptionUnit = DEFAULT_SERVICE_UNIT[serviceType];
    if (
      isConsumptionUnit(storedUnit) &&
      SERVICE_UNITS[serviceType].includes(storedUnit)
    ) {
      consumptionUnit = storedUnit;
    } else {
      // eslint-disable-next-line no-console
      console.warn(
        `⚠️ [safeTransformInvoiceToForm] Invalid unit "${storedUnit}" for service "${serviceType}", using default`,
      );
    }

    if (!invoice.start_date || !invoice.end_date) {
      throw new Error('Missing date fields');
    }

    // Validate date formats and ensure they're valid
    const startDate = new Date(invoice.start_date);
    const endDate = new Date(invoice.end_date);

    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      throw new Error('Invalid date format in invoice data');
    }

    if (endDate < startDate) {
      // eslint-disable-next-line no-console
      console.warn(
        '⚠️ [safeTransformInvoiceToForm] End date is before start date, swapping dates',
      );
      // Swap dates if end is before start
      const temp = invoice.start_date;
      invoice.start_date = invoice.end_date;
      invoice.end_date = temp;
    }

    // Handle currency - provide reasonable default if null/invalid
    let validCurrency = invoice.currency;
    if (
      !validCurrency ||
      !SUPPORTED_INVOICE_CURRENCIES.includes(validCurrency)
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        `⚠️ [safeTransformInvoiceToForm] Invalid or missing currency "${validCurrency}", using ${fallbackCurrency}`,
      );
      validCurrency = fallbackCurrency;
    }

    // Note: formatDateForInput moved to ../utils/formatters.ts as formatIsoDateForInput for consistency

    // Build form data with safe defaults
    const formData: InvoiceEditForm = {
      service_type: serviceType,
      consumption: invoice.consumption,
      consumption_unit: consumptionUnit,
      start_date: formatIsoDateForInput(invoice.start_date),
      end_date: formatIsoDateForInput(invoice.end_date),
      currency: validCurrency, // Use validated currency instead of raw value
    };

    // Add optional fields only if they exist and are valid
    if (
      typeof invoice.cost_total === 'number' &&
      !isNaN(invoice.cost_total) &&
      invoice.cost_total >= 0
    ) {
      formData.cost_total = invoice.cost_total;
    }

    if (
      invoice.vendor_name &&
      typeof invoice.vendor_name === 'string' &&
      invoice.vendor_name.trim()
    ) {
      formData.vendor_name = invoice.vendor_name.trim();
    }

    if (
      invoice.invoice_number &&
      typeof invoice.invoice_number === 'string' &&
      invoice.invoice_number.trim()
    ) {
      formData.invoice_number = invoice.invoice_number.trim();
    }

    if (
      invoice.issued_date &&
      typeof invoice.issued_date === 'string' &&
      invoice.issued_date.trim()
    ) {
      formData.issued_date = invoice.issued_date.trim();
    }

    if (
      invoice.supply_id &&
      typeof invoice.supply_id === 'string' &&
      invoice.supply_id.trim()
    ) {
      formData.supply_id = invoice.supply_id.trim();
    }

    return formData;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      '❌ [safeTransformInvoiceToForm] Transformation failed:',
      error,
    );
    // eslint-disable-next-line no-console
    console.error('❌ [safeTransformInvoiceToForm] Invoice data:', invoice);

    // Return minimal valid form data to prevent crashes
    const fallbackData: InvoiceEditForm = {
      service_type: 'electricidad', // Safe default
      consumption: 0,
      consumption_unit: 'kWh',
      start_date: getCurrentDateString(),
      end_date: getCurrentDateString(),
      currency: fallbackCurrency,
    };

    toast.error(
      'Los datos de la factura contienen errores. Se han aplicado valores predeterminados.',
    );
    return fallbackData;
  }
};

/**
 * Safely resets form with invoice data, handling all edge cases
 */
const safeResetForm = (
  reset: (data: InvoiceEditForm) => void,
  invoice: Invoice,
  fallbackCurrency: string,
  onError?: (error: Error) => void,
): void => {
  try {
    const formData = safeTransformInvoiceToForm(invoice, fallbackCurrency);
    reset(formData);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('❌ [safeResetForm] Form reset failed:', error);

    if (onError) {
      onError(
        error instanceof Error
          ? error
          : new Error('Unknown error during form reset'),
      );
    } else {
      toast.error(
        'Error al cargar los datos del formulario. Inténtalo de nuevo.',
      );
    }
  }
};

// ============================================================================
// Component
// ============================================================================

export const InvoiceResultsPage: React.FC<InvoiceResultsPageProps> = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const transactionId = searchParams.get('id');
  const transactionType = searchParams.get(
    'type',
  ) as FrontendTransactionType | null;
  const origin = searchParams.get('origin');
  const { currencySymbol, currencyCode } = useCurrency();

  // Debug logging for parameter analysis
  console.log('🔍 [InvoiceResultsPage] URL Parameters:', {
    transactionId,
    transactionType,
    origin,
    url: window.location.href,
  });

  // State management
  const [viewMode, setViewMode] = useState<ViewMode>('preview');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Transaction operations (unified for OCR and manual)
  const {
    updateTransaction,
    deleteTransaction,
    isLoading: isUpdating,
  } = useTransactionOperations();

  // First, try to infer transaction type if missing
  const {
    data: typeInference,
    isLoading: isInferringType,
    error: typeError,
  } = useTransactionType(transactionId);

  // Determine the actual transaction type to use
  const actualTransactionType =
    transactionType || typeInference?.transaction_type || null;

  // Note: Transaction type normalization is now handled in API service

  // Debug logging for type inference
  console.log('🔍 [InvoiceResultsPage] Type Inference State:', {
    isInferringType,
    typeError: typeError?.message,
    typeInference,
    transactionTypeFromURL: transactionType,
    actualTransactionType,
  });

  // Fetch transaction data using new unified hook (normalization handled in API service)
  const {
    data: transaction,
    isLoading: isLoadingTransaction,
    error: transactionError,
  } = useTransactionDetails(transactionId, actualTransactionType);

  // Debug logging for transaction loading
  console.log('📊 [InvoiceResultsPage] Query state:', {
    isLoading: isInferringType || isLoadingTransaction,
    hasData: !!transaction,
    hasError: !!(typeError || transactionError),
    transactionId,
    actualTransactionType,
    errorMessage: typeError?.message || transactionError?.message,
  });

  // Combine loading states
  const isLoading = isInferringType || isLoadingTransaction;
  const error = typeError || transactionError;

  // For backward compatibility, we'll call it 'invoice' in the rest of the component
  const invoice = transaction;

  // Form setup for editing with safe resolver
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty, errors },
    watch,
    setValue,
  } = useForm<InvoiceEditForm>({
    resolver: yupResolver(invoiceSchemas.invoiceEdit) as any,
    mode: 'onChange',
    defaultValues: {
      service_type: 'electricidad',
      consumption: 0,
      consumption_unit: 'kWh',
      start_date: getCurrentDateString(),
      end_date: getCurrentDateString(),
      currency: currencyCode,
    },
  });

  const watchedServiceType = watch('service_type');
  const watchedUnit = watch('consumption_unit');
  const watchedConsumption = watch('consumption');
  const watchedBagSize = watch('bag_size');
  const allowedUnits = useMemo<readonly ConsumptionUnit[]>(
    () => SERVICE_UNITS[watchedServiceType] ?? [],
    [watchedServiceType],
  );

  const unitOptions = useMemo(
    () =>
      allowedUnits.map((unit) => ({ value: unit, label: UNIT_LABELS[unit] })),
    [allowedUnits],
  );

  const bagKilograms =
    watchedUnit === 'bolsas' &&
    watchedBagSize &&
    Number.isFinite(watchedConsumption)
      ? calculateBagKilograms(watchedConsumption, watchedBagSize)
      : null;

  const originalUnitInvalid =
    !!invoice &&
    isServiceType(invoice.service_type) &&
    !isUnitAllowedForService(invoice.service_type, invoice.consumption_unit);

  // Keep the unit valid when the service type changes.
  useEffect(() => {
    if (allowedUnits.length > 0 && !allowedUnits.includes(watchedUnit)) {
      setValue('consumption_unit', DEFAULT_SERVICE_UNIT[watchedServiceType], {
        shouldValidate: true,
      });
    }
  }, [allowedUnits, watchedServiceType, watchedUnit, setValue]);

  // Update transaction handler using the new unified hook
  const handleUpdateInvoice = useCallback(
    async (data: InvoiceEditForm) => {
      if (!transactionId || !actualTransactionType) {
        toast.error('No se encontró el ID de la transacción o su tipo');
        return;
      }

      // Check if this transaction type can be edited
      if (!canEditTransaction(actualTransactionType)) {
        toast.error('Este tipo de transacción no se puede editar');
        return;
      }

      // Validate data before sending
      if (
        !data.service_type ||
        typeof data.consumption !== 'number' ||
        !data.start_date ||
        !data.end_date
      ) {
        toast.error('Datos requeridos faltantes');
        return;
      }

      try {
        await updateTransaction({
          id: transactionId,
          transactionType: actualTransactionType as 'ocr' | 'manual', // Safe cast since we checked canEditTransaction
          data,
        });

        const transactionLabel = getTransactionTypeLabel(actualTransactionType);
        toast.success(`${transactionLabel} actualizada correctamente`);
        setViewMode('preview');
      } catch (error: unknown) {
        // eslint-disable-next-line no-console
        console.error('❌ [handleUpdateTransaction] Update error:', error);

        // Provide more specific error messages based on error type
        const transactionLabel = getTransactionTypeLabel(
          actualTransactionType,
        ).toLowerCase();
        let errorMessage = `Error al actualizar la ${transactionLabel}`;

        if (error && typeof error === 'object' && 'response' in error) {
          const axiosError = error as any;
          if (axiosError.response?.status === 400) {
            errorMessage =
              'Los datos proporcionados no son válidos. Por favor revisa la información.';
          } else if (axiosError.response?.status === 403) {
            errorMessage = `No tienes permisos para editar esta ${transactionLabel}.`;
          } else if (axiosError.response?.status === 404) {
            errorMessage = `La ${transactionLabel} ya no existe o fue eliminada.`;
          } else if (axiosError.response?.status >= 500) {
            errorMessage = 'Error del servidor. Inténtalo de nuevo más tarde.';
          } else if (axiosError.response?.data?.detail) {
            errorMessage = axiosError.response.data.detail;
          }
        } else if (error && typeof error === 'object' && 'detail' in error) {
          errorMessage = (error as any).detail;
        } else if (error && typeof error === 'object' && 'message' in error) {
          errorMessage = (error as Error).message;
        }

        toast.error(errorMessage);
      } finally {
        setIsSubmitting(false);
      }
    },
    [transactionId, actualTransactionType, updateTransaction],
  );

  // Log success/error states and safely reset form
  React.useEffect(() => {
    if (invoice) {
      console.log(
        '✅ [InvoiceResultsPage] Invoice data loaded successfully:',
        invoice,
      );
      // Safely reset form with invoice data when in edit mode
      if (viewMode === 'edit') {
        safeResetForm(reset, invoice, currencyCode, (error) => {
          console.error('❌ [InvoiceResultsPage] Form reset error:', error);
          toast.error('Error al cargar los datos del formulario');
        });
      }
    }
  }, [invoice, reset, viewMode, currencyCode]);

  React.useEffect(() => {
    if (error) {
      console.error('❌ [InvoiceResultsPage] Query error:', error);
    }
  }, [error]);

  console.log('📊 [InvoiceResultsPage] Query state:', {
    isLoading,
    hasData: !!invoice,
    hasError: !!error,
    transactionId,
    transactionType,
    viewMode,
    isDirty,
    isSubmitting: isSubmitting || isUpdating,
  });

  // Debug: Log invoice data to check source_type and transaction_type
  if (invoice) {
    console.log('🔍 [InvoiceResultsPage] Invoice data debug:', {
      id: invoice.id,
      filename: invoice.filename,
      source_type: invoice.source_type,
      transaction_type: invoice.transaction_type,
      hasSourceType: 'source_type' in invoice,
      hasTransactionType: 'transaction_type' in invoice,
    });
  }

  // Handle navigation and actions
  const handleBack = useCallback(() => {
    if (viewMode === 'edit' && isDirty) {
      toast.error('Por favor, guarda o cancela los cambios antes de salir.');
    } else {
      // Navigate back based on origin parameter
      if (origin === 'transactions') {
        navigate('/transactions');
      } else {
        // Route to the canonical data-entry page directly to avoid the
        // ambiguous "/upload" route (which can resolve to either the standalone
        // upload page or a redirect).
        navigate('/data-entry');
      }
    }
  }, [navigate, viewMode, isDirty, origin]);

  // Handle delete transaction using unified delete system
  const handleDelete = useCallback(async () => {
    if (!transactionId || !actualTransactionType) return;

    // Check if this transaction type can be deleted
    if (!canDeleteTransaction(actualTransactionType)) {
      toast.error('Este tipo de transacción no se puede eliminar');
      return;
    }

    try {
      console.log('🗑️ [handleDelete] Attempting to delete transaction:', {
        transactionId,
        actualTransactionType,
        canDelete: canDeleteTransaction(actualTransactionType),
      });

      await deleteTransaction({
        id: transactionId,
        transactionType: actualTransactionType,
      });

      const transactionLabel = getTransactionTypeLabel(actualTransactionType);
      toast.success(`${transactionLabel} eliminada correctamente`);
      setShowDeleteModal(false);
      navigate('/invoices');
    } catch (error: any) {
      console.error('❌ [handleDelete] Error deleting transaction:', {
        transactionId,
        actualTransactionType,
        error,
        errorMessage: error?.response?.data?.detail || error?.message,
        status: error?.response?.status,
      });

      // Provide more specific error messages
      let errorMessage = 'Error al eliminar la transacción';
      if (error?.response?.status === 404) {
        errorMessage = 'La transacción no existe o ya ha sido eliminada';
      } else if (error?.response?.status === 403) {
        errorMessage = 'No tienes permisos para eliminar esta transacción';
      } else if (error?.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      }

      toast.error(errorMessage);
    }
  }, [transactionId, actualTransactionType, deleteTransaction, navigate]);

  const handleModeToggle = useCallback(
    (mode: ViewMode) => {
      if (mode === 'edit' && invoice) {
        // Safely reset form with current invoice data when entering edit mode
        safeResetForm(reset, invoice, currencyCode, (error) => {
          console.error('❌ [handleModeToggle] Form reset error:', error);
          toast.error('Error al cambiar a modo edición');
          // Don't change mode if form reset fails
          return;
        });
      }
      setViewMode(mode);
    },
    [invoice, reset, currencyCode],
  );

  const handleCancel = useCallback(() => {
    if (isDirty) {
      toast.error('Los cambios no guardados se perderán.');
    }
    // Reset to default values when canceling
    reset({
      service_type: 'electricidad',
      consumption: 0,
      consumption_unit: 'kWh',
      start_date: getCurrentDateString(),
      end_date: getCurrentDateString(),
      currency: currencyCode,
      notes: '',
    });
    setViewMode('preview');
  }, [isDirty, reset, currencyCode]);

  const handleSave = useCallback(
    async (data: InvoiceEditForm) => {
      console.log('💾 [InvoiceResultsPage] Saving invoice data:', data);

      // Additional client-side validation before submission
      try {
        // Validate date range
        const startDate = new Date(data.start_date);
        const endDate = new Date(data.end_date);

        if (endDate < startDate) {
          toast.error(
            'La fecha de fin debe ser posterior a la fecha de inicio',
          );
          return;
        }

        // Validate consumption is positive
        if (data.consumption < 0) {
          toast.error('El consumo no puede ser negativo');
          return;
        }

        // Validate cost if provided
        if (data.cost_total !== undefined && data.cost_total < 0) {
          toast.error('El costo total no puede ser negativo');
          return;
        }

        setIsSubmitting(true);
        await handleUpdateInvoice(data);
      } catch (validationError) {
        console.error('❌ [handleSave] Validation error:', validationError);
        toast.error('Error de validación en los datos del formulario');
      }
    },
    [handleUpdateInvoice],
  );

  const handleConfirm = useCallback(async () => {
    if (!transactionId) {
      toast.error('No se encontró el ID de la transacción.');
      return;
    }

    try {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.unifiedTransactions.all,
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.invoices.all,
        }),
        queryClient.refetchQueries({
          queryKey: queryKeys.dashboard.summary,
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.analytics.all,
        }),
      ]);

      toast.success('Factura confirmada');
      navigate('/transactions');
    } catch (error) {
      console.error(
        '❌ [InvoiceResultsPage] Error invalidating queries:',
        error,
      );
      toast.error(
        'No se pudo refrescar la lista de transacciones. Inténtalo nuevamente.',
      );
    }
  }, [transactionId, queryClient, navigate]);

  // Enhanced guard logic: Only fail if we can't get transaction ID or inferred type fails
  if (!transactionId) {
    console.error('❌ [InvoiceResultsPage] No transaction ID provided');
    return (
      <ResultsContainer>
        <ErrorContainer>
          <ErrorIcon>❌</ErrorIcon>
          <Title>Error de Parámetros</Title>
          <ErrorMessage>
            No se proporcionó un ID de transacción válido. Por favor, regresa a
            la lista de transacciones.
          </ErrorMessage>
          <Button variant="primary" onClick={() => navigate('/invoices')}>
            Volver a transacciones
          </Button>
        </ErrorContainer>
      </ResultsContainer>
    );
  }

  // If type inference failed and no explicit type provided, show error
  if (!transactionType && typeError) {
    console.error('❌ [InvoiceResultsPage] Failed to infer transaction type:', {
      transactionId,
      typeError: typeError?.message,
      actualTransactionType,
      typeInference,
    });
    return (
      <ResultsContainer>
        <ErrorContainer>
          <ErrorIcon>❌</ErrorIcon>
          <Title>Error de Parámetros</Title>
          <ErrorMessage>
            No se pudo determinar el tipo de transacción. La transacción podría
            no existir o no tener permisos para acceder.
          </ErrorMessage>
          <Button variant="primary" onClick={() => navigate('/invoices')}>
            Volver a transacciones
          </Button>
        </ErrorContainer>
      </ResultsContainer>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <ResultsContainer>
        <LoadingSpinner message="Cargando resultados de la factura..." />
      </ResultsContainer>
    );
  }

  // Error state
  if (error || !invoice) {
    console.error('❌ [InvoiceResultsPage] Transaction not found or error:', {
      error,
      transactionId,
      transactionType,
      hasTransaction: !!invoice,
    });

    // Provide more specific error messages
    const transactionTypeLabel = getTransactionTypeLabel(actualTransactionType);

    let errorTitle = `${transactionTypeLabel} no encontrada`;
    let errorMessage = `No se pudo encontrar la ${transactionTypeLabel.toLowerCase()} solicitada. Es posible que haya sido eliminada o que haya un error en el enlace.`;

    if (error && typeof error === 'object' && 'response' in error) {
      const axiosError = error as any;
      if (axiosError.response?.status === 404) {
        errorTitle = `${transactionTypeLabel} no encontrada`;
        errorMessage = `La ${transactionTypeLabel.toLowerCase()} solicitada no existe o ha sido eliminada.`;
      } else if (axiosError.response?.status === 403) {
        errorTitle = 'Acceso denegado';
        errorMessage = `No tienes permisos para ver esta ${transactionTypeLabel.toLowerCase()}.`;
      } else if (axiosError.response?.status >= 500) {
        errorTitle = 'Error del servidor';
        errorMessage =
          'Ha ocurrido un error en el servidor. Inténtalo de nuevo más tarde.';
      }
    }

    return (
      <ResultsContainer>
        <ErrorContainer>
          <ErrorIcon>
            <Icon name="file" size="lg" aria-hidden />
          </ErrorIcon>
          <Title>{errorTitle}</Title>
          <ErrorMessage>{errorMessage}</ErrorMessage>
          <Button variant="primary" onClick={handleBack}>
            Volver a escanear
          </Button>
        </ErrorContainer>
      </ResultsContainer>
    );
  }

  // Additional defensive check: Ensure invoice has required fields
  // For sales transactions, we don't require service_type or consumption
  const isSales = isSalesTransaction(actualTransactionType);
  const hasRequiredFields = isSales
    ? invoice.covers_count !== undefined && invoice.covers_count !== null
    : invoice.service_type && typeof invoice.consumption === 'number';

  if (!hasRequiredFields) {
    console.error('❌ [InvoiceResultsPage] Invoice missing required fields:', {
      invoice,
      actualTransactionType,
      isSales,
    });
    return (
      <ResultsContainer>
        <ErrorContainer>
          <ErrorIcon>
            <Icon name="warning" size="xl" aria-hidden />
          </ErrorIcon>
          <Title>Datos incompletos</Title>
          <ErrorMessage>
            La{' '}
            {isSalesTransaction(actualTransactionType)
              ? 'transacción de ventas'
              : 'factura'}{' '}
            no contiene toda la información necesaria. Los datos pueden estar
            corruptos.
          </ErrorMessage>
          <Button variant="primary" onClick={handleBack}>
            Volver a escanear
          </Button>
        </ErrorContainer>
      </ResultsContainer>
    );
  }

  // Render preview mode with defensive programming
  const renderPreviewMode = () => {
    try {
      return (
        <>
          <InvoiceGrid>
            {!isSalesTransaction(actualTransactionType) && (
              <FieldGroup>
                <FieldLabel>Servicio</FieldLabel>
                <FieldValue>
                  {invoice.service_type
                    ? formatServiceType(invoice.service_type)
                    : 'Tipo desconocido'}
                </FieldValue>
              </FieldGroup>
            )}

            <FieldGroup>
              <FieldLabel>Proveedor</FieldLabel>
              <FieldValue>
                {invoice.vendor_name || 'No especificado'}
              </FieldValue>
            </FieldGroup>

            <FieldGroup>
              <FieldLabel>Número de Factura</FieldLabel>
              <FieldValue>
                {invoice.invoice_number || 'No especificado'}
              </FieldValue>
            </FieldGroup>

            <FieldGroup>
              <FieldLabel>Fecha de Emisión</FieldLabel>
              <FieldValue>
                {invoice.issued_date
                  ? formatDate(invoice.issued_date)
                  : 'No especificada'}
              </FieldValue>
            </FieldGroup>

            <FieldGroup>
              <FieldLabel>Período de Consumo</FieldLabel>
              <FieldValue>
                {(() => {
                  try {
                    return `${formatDateShort(invoice.start_date)} - ${formatDateShort(invoice.end_date)}`;
                  } catch (dateError) {
                    console.warn(
                      '⚠️ [renderPreviewMode] Date formatting error:',
                      dateError,
                    );
                    return 'Fechas no disponibles';
                  }
                })()}
              </FieldValue>
            </FieldGroup>

            <FieldGroup>
              <FieldLabel>
                {isSalesTransaction(actualTransactionType)
                  ? 'Cubiertos'
                  : 'Consumo'}
              </FieldLabel>
              <FieldValue>
                {(() => {
                  try {
                    if (isSalesTransaction(actualTransactionType)) {
                      // For sales transactions, show covers count
                      return `${formatNumberEs(invoice.covers_count || 0)} cubiertos`;
                    } else {
                      // For utility transactions, show consumption
                      return `${formatNumberEs(invoice.consumption || 0)} ${invoice.consumption_unit || getUnit(invoice.service_type || 'electricidad')}`;
                    }
                  } catch (formatError) {
                    console.warn(
                      '⚠️ [renderPreviewMode] Number/unit formatting error:',
                      formatError,
                    );
                    if (isSalesTransaction(actualTransactionType)) {
                      return `${invoice.covers_count || 0} cubiertos`;
                    } else {
                      return `${invoice.consumption || 0} ${invoice.consumption_unit || 'unidades'}`;
                    }
                  }
                })()}
              </FieldValue>
            </FieldGroup>

            {invoice.cost_total && (
              <FieldGroup>
                <FieldLabel>Costo Total</FieldLabel>
                <FieldValue>
                  {(() => {
                    try {
                      return `${formatNumberEs(invoice.cost_total)} ${invoice.currency}`;
                    } catch (costError) {
                      console.warn(
                        '⚠️ [renderPreviewMode] Cost formatting error:',
                        costError,
                      );
                      return `${invoice.cost_total} ${invoice.currency || 'EUR'}`;
                    }
                  })()}
                </FieldValue>
              </FieldGroup>
            )}

            {invoice.supply_id && (
              <FieldGroup>
                <FieldLabel>ID de Suministro</FieldLabel>
                <FieldValue>{invoice.supply_id}</FieldValue>
              </FieldGroup>
            )}

            {(invoice.notes ||
              (invoice.ocr_confidence !== null &&
                invoice.ocr_confidence !== undefined &&
                invoice.ocr_confidence < 0.8)) && (
              <FieldGroup>
                <FieldLabel>Notas</FieldLabel>
                <FieldValue>
                  {invoice.notes || (
                    <span
                      style={{
                        color: 'var(--text-muted)',
                        fontStyle: 'italic',
                      }}
                    >
                      {invoice.ocr_confidence !== null &&
                      invoice.ocr_confidence !== undefined &&
                      invoice.ocr_confidence < 0.8
                        ? 'Confianza baja en OCR - revisar datos'
                        : 'Sin notas adicionales'}
                    </span>
                  )}
                </FieldValue>
              </FieldGroup>
            )}
          </InvoiceGrid>

          <ActionsSection>
            <BackButton variant="secondary" onClick={handleBack}>
              ← Volver
            </BackButton>
            {canEditTransaction(actualTransactionType) && (
              <EditButton
                variant="tertiary"
                onClick={() => handleModeToggle('edit')}
              >
                <Icon name="edit" size="sm" aria-hidden /> Editar
              </EditButton>
            )}
            {canDeleteTransaction(actualTransactionType) && (
              <Button
                variant="secondary"
                onClick={() => setShowDeleteModal(true)}
                style={{
                  backgroundColor: 'var(--danger-600)',
                  borderColor: 'var(--danger-600)',
                  color: 'white',
                }}
              >
                <Icon name="delete" size="sm" aria-hidden /> Eliminar
              </Button>
            )}
            <ConfirmButton variant="primary" onClick={handleConfirm}>
              <Icon name="confirm" size="sm" aria-hidden /> Confirmar
            </ConfirmButton>
          </ActionsSection>
        </>
      );
    } catch (renderError) {
      console.error('❌ [renderPreviewMode] Render error:', renderError);
      return (
        <div
          style={{
            padding: '2rem',
            textAlign: 'center',
            color: 'var(--text-muted)',
          }}
        >
          <p>
            Error al mostrar los datos de la factura. Los datos pueden estar
            corruptos.
          </p>
          <Button variant="secondary" onClick={() => window.location.reload()}>
            Recargar página
          </Button>
        </div>
      );
    }
  };

  // Render edit mode
  const renderEditMode = () => (
    <form onSubmit={handleSubmit(handleSave)}>
      <EditFormContainer>
        <FormSection>
          <SectionTitle>Información Básica</SectionTitle>

          <ServiceTypeSelect
            name="service_type"
            control={control}
            label="Tipo de Servicio"
            required
            {...(errors.service_type && { error: errors.service_type })}
            helperText="Selecciona el tipo de servicio de la factura"
          />

          <Controller
            name="vendor_name"
            control={control}
            render={({ field, fieldState }) => (
              <FormField
                {...field}
                value={field.value || ''}
                label="Proveedor"
                placeholder="Nombre del proveedor"
                error={fieldState.error}
                helperText="Nombre de la empresa proveedora del servicio"
              />
            )}
          />

          <Controller
            name="invoice_number"
            control={control}
            render={({ field, fieldState }) => (
              <FormField
                {...field}
                value={field.value || ''}
                label="Número de Factura"
                placeholder="Ej: FAC-2024-001"
                error={fieldState.error}
                helperText="Número identificador de la factura"
              />
            )}
          />

          <Controller
            name="supply_id"
            control={control}
            render={({ field, fieldState }) => (
              <FormField
                {...field}
                value={field.value || ''}
                label="ID de Suministro"
                placeholder="Identificador del suministro"
                error={fieldState.error}
                helperText="Código de identificación del punto de suministro"
              />
            )}
          />
        </FormSection>

        <FormSection>
          <SectionTitle>Fechas</SectionTitle>

          <Controller
            name="issued_date"
            control={control}
            render={({ field, fieldState }) => (
              <FormField
                {...field}
                value={field.value || ''}
                type="date"
                label="Fecha de Emisión"
                error={fieldState.error}
                helperText="Fecha en que se emitió la factura"
              />
            )}
          />

          <Controller
            name="start_date"
            control={control}
            render={({ field, fieldState }) => (
              <FormField
                {...field}
                type="date"
                label="Fecha de Inicio del Período"
                required
                error={fieldState.error}
                helperText="Inicio del período de consumo"
              />
            )}
          />

          <Controller
            name="end_date"
            control={control}
            render={({ field, fieldState }) => (
              <FormField
                {...field}
                type="date"
                label="Fecha de Fin del Período"
                required
                error={fieldState.error}
                helperText="Fin del período de consumo"
              />
            )}
          />
        </FormSection>

        <FormSection>
          <SectionTitle>Consumo y Costos</SectionTitle>

          <Controller
            name="consumption"
            control={control}
            render={({ field, fieldState }) => (
              <FormField
                {...field}
                type="number"
                step="0.01"
                min="0"
                label="Consumo"
                required
                error={fieldState.error}
                helperText={`Cantidad consumida en ${watchedUnit}`}
                endAdornment={watchedUnit}
              />
            )}
          />

          {originalUnitInvalid && (
            <UnitWarning role="note">
              <Icon
                name="warning"
                size="sm"
                color="var(--warning-600)"
                aria-hidden
              />
              <span>
                La unidad original «{invoice?.consumption_unit}» no es válida
                para este servicio, por eso este registro no se incluye en los
                totales. Revisa el consumo y elige la unidad correcta.
              </span>
            </UnitWarning>
          )}

          <SelectField
            name="consumption_unit"
            control={control}
            label="Unidad de Consumo"
            required
            options={unitOptions}
            error={errors.consumption_unit}
            helperText="Unidad en la que aparece el consumo en la factura"
          />

          {watchedUnit === 'bolsas' && (
            <SelectField
              name="bag_size"
              control={control}
              label="Tamaño de bolsa"
              required
              placeholder="Selecciona un tamaño"
              options={BAG_SIZE_SELECT_OPTIONS}
              error={errors.bag_size}
              helperText={
                bagKilograms !== null
                  ? `Se guardará como ${formatNumberEs(bagKilograms, 1)} kg`
                  : 'Las bolsas se convierten a kg según su tamaño'
              }
            />
          )}

          <Controller
            name="cost_total"
            control={control}
            render={({ field, fieldState }) => (
              <FormField
                {...field}
                value={field.value || ''}
                type="number"
                step="0.01"
                min="0"
                label="Costo Total"
                error={fieldState.error}
                helperText="Importe total de la factura"
                startAdornment={currencySymbol}
              />
            )}
          />

          {/* Currency is now auto-populated from user profile */}
        </FormSection>

        <FormSection>
          <SectionTitle>📝 Notas (Opcional)</SectionTitle>
          <Controller
            name="notes"
            control={control}
            render={({ field, fieldState }) => (
              <div>
                <label
                  htmlFor="notes-input"
                  style={{
                    display: 'block',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--text-muted)',
                    marginBottom: 'var(--space-xs)',
                  }}
                >
                  Notas adicionales
                </label>
                <textarea
                  {...field}
                  id="notes-input"
                  value={field.value || ''}
                  placeholder="Ej: Factura de emergencia, lectura estimada, cambio de proveedor..."
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '1rem 1.25rem',
                    border: `2px solid ${fieldState.error ? 'var(--danger-600)' : 'var(--border)'}`,
                    borderRadius: '12px',
                    fontSize: '1.1rem',
                    lineHeight: '1.4',
                    color: 'var(--text-primary)',
                    backgroundColor: 'var(--surface)',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                    minHeight: '80px',
                  }}
                  onChange={(e) => field.onChange(e.target.value)}
                />
                {fieldState.error && (
                  <div
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--danger-600)',
                      marginTop: 'var(--space-xs)',
                    }}
                  >
                    {fieldState.error.message}
                  </div>
                )}
                <div
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--text-muted)',
                    marginTop: 'var(--space-xs)',
                  }}
                >
                  Información adicional sobre la factura (máximo 500 caracteres)
                </div>
              </div>
            )}
          />
        </FormSection>
      </EditFormContainer>

      <SaveCancelActions>
        <Button
          type="button"
          variant="secondary"
          onClick={handleCancel}
          disabled={isSubmitting}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="primary"
          disabled={isSubmitting || !isDirty}
          loading={isSubmitting}
        >
          {isSubmitting ? 'Guardando...' : 'Guardar Cambios'}
        </Button>
      </SaveCancelActions>
    </form>
  );

  return (
    <ResultsContainer>
      <Header>
        <Title>Revisar factura</Title>
        <Subtitle>
          {viewMode === 'preview'
            ? isSalesTransaction(actualTransactionType)
              ? 'Estos son los datos de la transacción de ventas. Las transacciones de ventas son de solo lectura.'
              : 'La IA ha procesado tu factura y extraído los siguientes datos. Revisa la información y confírmala si es correcta.'
            : 'Edita los datos extraídos de la factura. Los campos marcados con * son obligatorios.'}
        </Subtitle>
      </Header>

      <ResultsGrid>
        <InvoiceCard>
          <InvoiceHeader>
            <InvoiceTitle>
              {isSalesTransaction(actualTransactionType)
                ? `Ventas - ${getTransactionTypeLabel(actualTransactionType)}`
                : `${invoice.service_type ? formatServiceType(invoice.service_type) : 'Servicio'} - ${invoice.filename || 'Sin archivo'}`}
            </InvoiceTitle>
            {!isSalesTransaction(actualTransactionType) &&
              invoice.ocr_confidence !== null &&
              invoice.ocr_confidence !== undefined && (
                <StatusBadge
                  confidence={formatConfidence(invoice.ocr_confidence)}
                >
                  {Math.round(formatConfidence(invoice.ocr_confidence))}%
                  confianza
                </StatusBadge>
              )}
            {isSalesTransaction(actualTransactionType) && (
              <StatusBadge confidence={100}>
                {getTransactionTypeLabel(actualTransactionType)}
              </StatusBadge>
            )}
          </InvoiceHeader>

          {/* AI Confidence Indicator - Only show for OCR scanned invoices */}
          {!isSalesTransaction(actualTransactionType) &&
            invoice.transaction_type !== 'manual' &&
            invoice.source_type !== 'manual' &&
            invoice.ocr_confidence !== null &&
            invoice.ocr_confidence !== undefined && (
              <div style={{ marginBottom: 'var(--space-lg)' }}>
                <h4
                  style={{
                    margin: '0 0 var(--space-sm) 0',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--text-muted)',
                  }}
                >
                  CONFIANZA DE LA IA
                </h4>
                <ConfidenceIndicator confidence={invoice.ocr_confidence} />
                {formatConfidence(invoice.ocr_confidence) < 70 && (
                  <p
                    style={{
                      fontSize: 'var(--font-size-sm)',
                      color: 'var(--warning-600)',
                      margin: 'var(--space-xs) 0 0 0',
                    }}
                  >
                    <Icon name="warning" size="sm" aria-hidden /> Confianza
                    baja: Revisa cuidadosamente los datos extraídos
                  </p>
                )}
              </div>
            )}

          {/* Render content based on mode */}
          {viewMode === 'preview' ? renderPreviewMode() : renderEditMode()}
        </InvoiceCard>
      </ResultsGrid>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowDeleteModal(false)}
        >
          <div
            style={{
              backgroundColor: 'var(--surface)',
              padding: 'var(--space-xl)',
              borderRadius: 'var(--radius-lg)',
              border: '1px solid var(--border)',
              maxWidth: '400px',
              width: '90%',
              boxShadow: '0 10px 25px rgba(0, 0, 0, 0.25)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              style={{ textAlign: 'center', marginBottom: 'var(--space-lg)' }}
            >
              <div
                style={{ fontSize: '2rem', marginBottom: 'var(--space-md)' }}
              >
                <Icon name="delete" size="xl" aria-hidden />
              </div>
              <h3
                style={{
                  margin: '0 0 var(--space-sm) 0',
                  color: 'var(--text-primary)',
                }}
              >
                Eliminar Factura
              </h3>
              <p style={{ margin: 0, color: 'var(--text-muted)' }}>
                ¿Estás seguro de que quieres eliminar esta factura? Esta acción
                no se puede deshacer.
              </p>
            </div>
            <div
              style={{
                display: 'flex',
                gap: 'var(--space-md)',
                justifyContent: 'center',
              }}
            >
              <Button
                variant="secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                Cancelar
              </Button>
              <Button
                variant="secondary"
                onClick={handleDelete}
                style={{
                  backgroundColor: 'var(--danger-600)',
                  borderColor: 'var(--danger-600)',
                  color: 'white',
                }}
              >
                <Icon name="delete" size="sm" aria-hidden /> Eliminar
              </Button>
            </div>
          </div>
        </div>
      )}
    </ResultsContainer>
  );
};

export default InvoiceResultsPage;
